import { Permission } from '~/acl.ts';
import { api } from '~/api.ts';
import {
    AccountingUserRole,
    AdminUserRole,
    ApplicantUserRole,
    AppUserRole,
    CandidateExperiment,
    CandidateStatus,
    CandidateUserRole,
    Contract,
    ContractorUserRole,
    CustomerInterviewerUserRole,
    CustomerLeadUserRole,
    CustomerManagerUserRole,
    CustomerRecruiterUserRole,
    CustomerUserRole,
    DisqualifiedCandidateStatus,
    NoneHealthInsuranceProvider,
    OnboardFlow,
    SuperadminUserRole,
    TalentFreelancerUserRole,
    TalentManagerUserRole,
    TalentOperationUserRole,
    TalentUserRole,
    User,
    UserRole,
} from '~/types/domain';
import { fullName } from '~/utils/format';

export const TALENT_ROLES = [
    SuperadminUserRole,
    AdminUserRole,
    TalentUserRole,
    TalentManagerUserRole,
    TalentOperationUserRole,
    TalentFreelancerUserRole,
];
const CUSTOMER_ROLES = [
    CustomerUserRole,
    CustomerInterviewerUserRole,
    CustomerManagerUserRole,
    CustomerRecruiterUserRole,
];
export const REMOTELY_ROLES = [...TALENT_ROLES, AccountingUserRole, AppUserRole];

export type AuthenticatedUser = User & {
    company?: string;
    company_logo?: string;
    contract?: Contract;
    permissions: Permission[];
    linkedin_url?: string;
    github_url?: string;
    terms_accepted: boolean;
    country: string;
    obfuscated_id: string;
    candidate_id?: number;
    open_to_opportunities?: boolean;
    has_invoices?: boolean;
    profile_complete: boolean;
    onboard_flow?: OnboardFlow;
    experiments: CandidateExperiment[];
    in_network: boolean;
    candidate_status: CandidateStatus;
};

export async function getAuthenticatedUser(): Promise<AuthenticatedUser> {
    return api<AuthenticatedUser>('/users/me');
}

export interface AuthUser extends AuthenticatedUser {
    authenticated: boolean;
    customerId: number | undefined;
    ownCustomerId: number | undefined;
    hasRole(role: UserRole): boolean;
    hasAnyRole(...roles: UserRole[]): boolean;
    isContractor(): boolean;
    isCandidate(): boolean;
    hasContract(): boolean;
    isCustomer(): boolean;
    isCustomerRecruiter(): boolean;
    isInterviewer(): boolean;
    isAdmin(): boolean;
    isCustomerLead(): boolean;
    isTalent(): boolean;
    isTalentFreelancer(): boolean;
    isCustomerLead(): boolean;
    isRemotely(): boolean;
    isApplicant(): boolean;
    isUsBased(): boolean;
    hasInvoices(): boolean;
    hasBenefit(benefit: 'pto' | 'severance' | 'insurance'): boolean;
    hasAccess(...permissions: Permission[]): boolean;
    fullName: string;
    isExContractor(): boolean;
}

export function newUser(user: AuthenticatedUser | null): AuthUser {
    return {
        ...(user || {}),

        authenticated: Boolean(user),

        get customerId() {
            const userCustomerId = this.customer_id;
            const userContractCustomerId = this.contract?.customer_id;
            return userCustomerId || userContractCustomerId;
        },

        get ownCustomerId(): number | undefined {
            return this.customer_id;
        },

        hasRole(role: UserRole): boolean {
            return this.roles?.includes(role);
        },

        hasAnyRole(...roles: UserRole[]): boolean {
            for (const r of roles) {
                if (this.hasRole(r)) {
                    return true;
                }
            }
            return false;
        },

        isContractor() {
            return this.hasRole(ContractorUserRole);
        },

        isCandidate() {
            return this.hasRole(CandidateUserRole);
        },

        hasContract() {
            return !!this?.contract;
        },

        isCustomer() {
            return this.hasAnyRole(...CUSTOMER_ROLES);
        },

        isCustomerRecruiter() {
            return this.hasAnyRole(CustomerRecruiterUserRole);
        },

        isInterviewer() {
            return this.hasRole(CustomerInterviewerUserRole);
        },

        isAdmin() {
            return this.hasRole(AdminUserRole) || this.hasRole(SuperadminUserRole);
        },

        isCustomerLead() {
            return this.hasRole(CustomerLeadUserRole);
        },

        isTalent() {
            return this.hasAnyRole(...TALENT_ROLES);
        },

        isTalentFreelancer() {
            return this.hasRole(TalentFreelancerUserRole);
        },

        isRemotely() {
            return this.hasAnyRole(...REMOTELY_ROLES);
        },

        isApplicant() {
            return this.hasRole(ApplicantUserRole);
        },

        isUsBased() {
            return this.country === 'US';
        },

        hasInvoices() {
            return Boolean(this.has_invoices);
        },

        hasBenefit(benefit: 'pto' | 'severance' | 'insurance'): boolean {
            if (!this.contract?.benefits) return true;
            switch (benefit) {
                case 'pto':
                    return Boolean(this.contract.benefits.pto);
                case 'severance':
                    return Boolean(this.contract.benefits.severance);
                case 'insurance':
                    return (
                        Boolean(this.contract.benefits.insurance_provider) &&
                        this.contract.benefits.insurance_provider !== NoneHealthInsuranceProvider
                    );
            }
        },

        hasAccess(...permissions: Permission[]) {
            if (!this.authenticated) {
                return false;
            }
            if (permissions.length === 0) {
                return true;
            }

            for (const p of permissions) {
                for (const up of this.permissions) {
                    if (p === up) return true;
                }
            }

            return false;
        },

        get fullName() {
            return fullName(this);
        },

        isExContractor() {
            return !this.in_network && this.candidate_status === DisqualifiedCandidateStatus;
        },
    } as AuthUser;
}
